/**
 * Pilule de menu HolyDougz — UNE pilule qui glisse de lien en lien dans le menu desktop.
 *
 * L'élément <hd-menu-pill> est posé dans `nav.menu-list` (light DOM, hors de l'overflow-list
 * qui compterait tout enfant comme un item). Il ne fait que recevoir des variables CSS
 * (--hd-pill-x/y/w/h/o/s) écrites ici ; le mouvement est en CSS (snippets/hd-styles.liquid).
 * Au repos : sur le lien actif s'il existe, sinon effacée sur place.
 */
const SELECTOR = 'header-menu nav.menu-list';

class HdMenuPill extends HTMLElement {
  /** @type {HTMLElement | null} */
  #nav = null;
  /** @type {ResizeObserver | null} */
  #resizer = null;
  #visible = false;

  connectedCallback() {
    this.setAttribute('aria-hidden', 'true');
    const nav = this.closest('nav.menu-list');
    if (!(nav instanceof HTMLElement)) return;
    this.#nav = nav;

    nav.addEventListener('pointerover', this.#onOver);
    nav.addEventListener('pointerleave', this.#onLeave);
    nav.addEventListener('focusin', this.#onOver);
    nav.addEventListener('focusout', this.#onFocusOut);
    this.#resizer = new ResizeObserver(() => this.#rest(true));
    this.#resizer.observe(nav);
    nav.querySelector('overflow-list')?.shadowRoot?.querySelector('slot')?.addEventListener('slotchange', () => this.#rest(true));

    this.#rest(true);
  }

  disconnectedCallback() {
    const nav = this.#nav;
    if (!nav) return;
    nav.removeEventListener('pointerover', this.#onOver);
    nav.removeEventListener('pointerleave', this.#onLeave);
    nav.removeEventListener('focusin', this.#onOver);
    nav.removeEventListener('focusout', this.#onFocusOut);
    this.#resizer?.disconnect();
  }

  /** @param {Event} event */
  #onOver = (event) => {
    const link = this.#linkFrom(event.target);
    if (link) this.#moveTo(link);
  };

  #onLeave = () => this.#rest(false);

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.#linkFrom(event.relatedTarget)) this.#rest(false);
  };

  /** @param {EventTarget | null} target */
  #linkFrom(target) {
    if (!(target instanceof Element)) return null;
    const link = target.closest('.menu-list__link');
    if (!(link instanceof HTMLElement)) return null;
    // Les liens repliés dans « Plus » ne sont pas sur le rail.
    if (link.closest('[slot="overflow"]')) return null;
    return link;
  }

  /** @param {HTMLElement} link */
  #moveTo(link, jump = false) {
    const nav = this.#nav;
    if (!nav) return;
    const n = nav.getBoundingClientRect();
    const r = link.getBoundingClientRect();
    if (r.width === 0) return;

    const wasHidden = !this.#visible;
    if (wasHidden || jump) this.toggleAttribute('data-jump', true);

    this.style.setProperty('--hd-pill-x', `${r.left - n.left}px`);
    this.style.setProperty('--hd-pill-y', `${r.top - n.top}px`);
    this.style.setProperty('--hd-pill-w', `${r.width}px`);
    this.style.setProperty('--hd-pill-h', `${r.height}px`);

    if (wasHidden || jump) {
      // Se placer sans glisser, puis apparaître avec un léger ressort.
      this.style.setProperty('--hd-pill-s', '0.94');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.removeAttribute('data-jump');
          this.style.setProperty('--hd-pill-s', '1');
        });
      });
    }
    this.style.setProperty('--hd-pill-o', '1');
    this.#visible = true;
  }

  /** @param {boolean} jump */
  #rest(jump) {
    const nav = this.#nav;
    if (!nav) return;
    const active = nav.querySelector('.menu-list__link--active');
    if (active instanceof HTMLElement && !active.closest('[slot="overflow"]')) {
      this.#moveTo(active, jump);
      return;
    }
    this.style.setProperty('--hd-pill-o', '0');
    this.style.setProperty('--hd-pill-s', '0.94');
    this.#visible = false;
  }
}

if (!customElements.get('hd-menu-pill')) customElements.define('hd-menu-pill', HdMenuPill);

function mount() {
  for (const nav of document.querySelectorAll(SELECTOR)) {
    if (!nav.querySelector(':scope > hd-menu-pill')) nav.append(document.createElement('hd-menu-pill'));
  }
}

mount();
document.addEventListener('DOMContentLoaded', mount);
window.addEventListener('load', mount);
document.addEventListener('shopify:section:load', mount);
window.addEventListener('pageshow', mount);

// Horizon ré-hydrate et morphe l'en-tête (section-hydration.js, morph.js) : notre élément
// peut être retiré ou arriver après nous. On remonte à chaque remaniement du groupe d'en-tête.
const group = document.getElementById('header-group');
if (group) new MutationObserver(mount).observe(group, { childList: true, subtree: true });
